import { MAX_ATTACHMENT_BATCH_BYTES, validateAttachmentBatch } from '@21k/shared';
import { useRef, useState } from 'react';

import {
  ATTACHMENT_ACCEPT,
  formatFileSize,
  getAssetType,
  MAX_ATTACHMENT_FILE_BYTES,
} from '../lib/assetUtils';

type Props = {
  files: File[];
  onChange: (files: File[]) => void;
  disabled?: boolean;
  id?: string;
  label?: string;
  iconOnly?: boolean;
  chipsOnly?: boolean;
};

function fileTypeIcon(mimeType: string): string {
  const type = getAssetType(mimeType);
  if (type === 'image') return '🖼️';
  if (type === 'pdf') return '📄';
  if (type === 'doc') return '📝';
  return '📎';
}

export default function AttachmentPicker({
  files,
  onChange,
  disabled,
  id = 'attachment-picker',
  label = 'Attach files',
  iconOnly = false,
  chipsOnly = false,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState('');

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = Array.from(e.target.files ?? []);
    if (picked.length === 0) return;

    const valid = picked.filter((f) => f.size <= MAX_ATTACHMENT_FILE_BYTES);
    const oversized = picked.filter((f) => f.size > MAX_ATTACHMENT_FILE_BYTES);
    if (oversized.length > 0) {
      setError(`${oversized.length} file(s) exceed 10MB and were not added.`);
    } else {
      setError('');
    }

    const merged = [...files, ...valid];
    const batchError = validateAttachmentBatch(merged);
    if (batchError) {
      setError(batchError);
      e.target.value = '';
      return;
    }

    onChange(merged);
    e.target.value = '';
  }

  function removeAt(index: number) {
    onChange(files.filter((_, i) => i !== index));
    setError('');
  }

  const totalBytes = files.reduce((sum, file) => sum + file.size, 0);

  const chips = files.length > 0 && (
    <div className="pending-file-chips">
      {files.map((file, index) => (
        <div key={`${file.name}-${index}`} className="pending-file-chip">
          <span aria-hidden>{fileTypeIcon(file.type)}</span>
          <span className="pending-file-chip-name">{file.name}</span>
          <button
            type="button"
            className="pending-file-chip-remove"
            onClick={() => removeAt(index)}
            disabled={disabled}
            aria-label={`Remove ${file.name}`}
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );

  if (chipsOnly) {
    return (
      <div className="attachment-picker">
        {chips}
        {files.length > 0 && (
          <span className="attachment-meta">
            {formatFileSize(totalBytes)} / {formatFileSize(MAX_ATTACHMENT_BATCH_BYTES)} total
          </span>
        )}
        {error && <div className="attachment-error">{error}</div>}
      </div>
    );
  }

  return (
    <div className="attachment-picker">
      <input
        ref={inputRef}
        id={id}
        type="file"
        multiple
        accept={ATTACHMENT_ACCEPT}
        hidden
        disabled={disabled}
        onChange={handleChange}
      />
      {iconOnly ? (
        <button
          type="button"
          className="conv-attach-btn"
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
          title="Attach files"
          aria-label="Attach files"
        >
          📎
        </button>
      ) : (
        <button
          type="button"
          className="btn btn-secondary btn-sm attach-btn"
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
        >
          📎 {label}
        </button>
      )}
      {!iconOnly && chips}
      {!iconOnly && files.length > 0 && (
        <span className="attachment-meta">
          {formatFileSize(totalBytes)} / {formatFileSize(MAX_ATTACHMENT_BATCH_BYTES)} total
        </span>
      )}
      {error && <div className="attachment-error">{error}</div>}
    </div>
  );
}
